import { ApiResponse } from '@stores/ApiResponse';
import { getErrorMessage } from '@utils/errorMessages';

/** What the server did to the participant list when a team was created or saved. */
export type GroupSaveSummary = {
  participantsAdded: number;
  newAccounts: number;
  alreadyInCohort: number;
  rolesUpdated: number;
  /** Steps that failed after the save committed, e.g. an invitation that couldn't be sent. */
  deliveryFailures?: string[];
};

export type GroupSaveVerb = 'created' | 'saved';

/**
 * saved: everything worked. savedWithProblems: the team saved but a follow-up step failed.
 * failed: nothing was saved. unknown: the server never confirmed, so it may or may not have saved.
 */
export type GroupSaveOutcome = 'saved' | 'savedWithProblems' | 'failed' | 'unknown';

/** Everything the admin is shown after saving a team. */
export type GroupSaveResult = {
  groupName: string;
  verb: GroupSaveVerb;
  outcome: GroupSaveOutcome;
  title: string;
  message: string;
  /** What changed, one line each. */
  changes: string[];
  /** Follow-up steps that failed after the team itself was saved. */
  problems: string[];
};

// The server may still be working after the browser stops waiting, so the save may or may not have landed
const NO_ANSWER_STATUS_CODES = [408, 502, 504];

const count = (n: number, noun: string) => `${n} ${noun}${n === 1 ? '' : 's'}`;

const describeChanges = (summary?: GroupSaveSummary) => {
  if (!summary) return [];

  const changes: string[] = [];
  if (summary.participantsAdded > 0) changes.push(`${count(summary.participantsAdded, 'participant')} invited`);
  if (summary.newAccounts > 0) changes.push(`${count(summary.newAccounts, 'new account')} created`);
  if (summary.rolesUpdated > 0) changes.push(`${count(summary.rolesUpdated, 'role')} updated`);
  if (summary.alreadyInCohort > 0) changes.push(`${summary.alreadyInCohort} already on the team and skipped`);
  return changes.length > 0 ? changes : ['No participant changes'];
};

const savedResult = (
  groupName: string,
  verb: GroupSaveVerb,
  changes: string[],
  problems: string[]
): GroupSaveResult => {
  if (problems.length === 0) {
    return {
      groupName,
      verb,
      outcome: 'saved',
      title: `Team ${verb}`,
      message: `"${groupName}" was ${verb}.`,
      changes,
      problems,
    };
  }
  return {
    groupName,
    verb,
    outcome: 'savedWithProblems',
    title: `Team ${verb}, with problems`,
    message: `"${groupName}" was ${verb}, but some follow-up steps failed. The team itself is saved.`,
    changes,
    problems,
  };
};

export const describeGroupSaveSuccess = (
  groupName: string,
  verb: GroupSaveVerb,
  summary?: GroupSaveSummary
): GroupSaveResult => savedResult(groupName, verb, describeChanges(summary), summary?.deliveryFailures ?? []);

/** Adds follow-up problems (e.g. a failed survey sync) to a team that saved. */
export const withProblems = (result: GroupSaveResult, problems: string[]): GroupSaveResult =>
  savedResult(result.groupName, result.verb, result.changes, [...result.problems, ...problems]);

export const describeGroupSaveFailure = (
  groupName: string,
  verb: GroupSaveVerb,
  response: ApiResponse
): GroupSaveResult => {
  // status 'ERROR' means the request never got a response (network failure)
  if (response.status === 'ERROR' || NO_ANSWER_STATUS_CODES.includes(response.statusCode)) {
    return {
      groupName,
      verb,
      outcome: 'unknown',
      title: 'Save status unknown',
      message:
        `Lost contact with the server before it confirmed the save, so "${groupName}" may or may not have been ${verb}. ` +
        'Refresh the team list to check before trying again.',
      changes: [],
      problems: [],
    };
  }

  // The cohort endpoints explain 409s and 500s themselves, including what was rolled back
  const serverMessage = response.result?.message;
  const explainedByServer =
    (response.statusCode === 409 || response.statusCode === 500) && typeof serverMessage === 'string';
  return {
    groupName,
    verb,
    outcome: 'failed',
    title: `Team wasn't ${verb}`,
    message: explainedByServer
      ? serverMessage
      : `"${groupName}" wasn't ${verb}. ${getErrorMessage(response.statusCode).message}`,
    changes: [],
    problems: [],
  };
};
